import Translations from "@translate/components/Sender/VoiceRecorder";
import { cn } from "utils";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { VoiceWaveform } from "./VoiceWaveform";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface VoiceRecorderProps {
  phase: "recording" | "transcribing";
  onStop: () => void;
  onDirectSend: () => void;
  onCancelTranscribe: () => void;
  analyserNode: AnalyserNode | null;
}

export function VoiceRecorder({
  phase,
  onStop,
  onDirectSend,
  onCancelTranscribe,
  analyserNode,
}: VoiceRecorderProps) {
  const isRecording = phase === "recording";
  const isTranscribing = phase === "transcribing";

  const buttonBg = isRecording
    ? "bg-background_white_overlay100"
    : "bg-background_white_overlay70";

  return (
    <div
      className={cn(
        "flex flex-row items-center gap-3 p-3",
        "rounded-4 border border-border_white_transparent5",
        "bg-background_solid_neutral_primary",
        "shadow-[0px_0px_16px_rgba(0,0,0,0.08)]",
      )}
    >
      {/* Left button: Stop (■) during recording, Cancel (×) during transcribing */}
      {isRecording && (
        <button
          type="button"
          onClick={onStop}
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
            buttonBg,
          )}
          aria-label="Stop"
        >
          <div className="w-3 h-3 rounded-sm bg-content_inverse_primary" />
        </button>
      )}
      {isTranscribing && (
        <button
          type="button"
          onClick={onCancelTranscribe}
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
            buttonBg,
          )}
          aria-label="Cancel"
        >
          <Icon
            name="close"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
          />
        </button>
      )}

      {/* Center: waveform or transcribing text */}
      {isRecording && <VoiceWaveform analyserNode={analyserNode} />}
      {isTranscribing && (
        <span className="flex-1 text-center body_l text-content_quaternary">
          {I18n.t("Transcribing....")}
        </span>
      )}

      {/* Right button: Send during recording, Loading during transcribing */}
      {isRecording && (
        <button
          type="button"
          onClick={onDirectSend}
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
            buttonBg,
          )}
          aria-label="Send"
        >
          <Icon
            name="arrow-right"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className="-rotate-90"
          />
        </button>
      )}
      {isTranscribing && (
        <div
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
            buttonBg,
          )}
        >
          <Icon
            name="loading"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className="animate-spin"
          />
        </div>
      )}
    </div>
  );
}
